import { Config, Constant } from '@genial/config';
import { Utils } from '@genial/utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FlagProvider, IConfig } from '@unleash/proxy-client-react';
import * as Font from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import React, { useCallback, useEffect, useState } from 'react';
import { hotjar } from 'react-hotjar';
import { Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { RecoilRoot } from 'recoil';

import { NavigationContainer } from './src/routes/NavigationContainer';
import { initFirebase } from './src/utils/firebaseConfig';

SplashScreen.preventAutoHideAsync();

if (Platform.OS === 'web') {
  hotjar.initialize({ id: Constant.HotjarId, sv: 6 });
}

const unleashConfig: IConfig = {
  url: Config.UnleashConfigUrl,
  clientKey: Config.UnleashConfigClientKey,
  appName: Config.UnleashConfigAppName,
  storageProvider: {
    save: (name, data) => AsyncStorage.setItem(name, JSON.stringify(data)),
    get: async (name) => {
      const data = await AsyncStorage.getItem(name);
      return data ? JSON.parse(data) : undefined;
    },
  },
};

const loadFonts = async () =>
  await Font.loadAsync({
    'SourceSansPro-Regular': require('./assets/fonts/SourceSansPro-Regular.ttf'),
    'SourceSansPro-SemiBold': require('./assets/fonts/SourceSansPro-SemiBold.ttf'),
    'SourceSansPro-Bold': require('./assets/fonts/SourceSansPro-Bold.ttf'),
    'SourceSansPro-Light': require('./assets/fonts/SourceSansPro-Light.ttf'),
    'SourceSansPro-Italic': require('./assets/fonts/SourceSansPro-Italic.ttf'),
  });

const App = () => {
  const [isReady, setIsReady] = useState(false);

  const onStart = useCallback(async () => {
    await loadFonts();
    await SplashScreen.hideAsync();
    initFirebase();

    setIsReady(true);
  }, []);

  const cleanLocalCache = useCallback(
    () => async () => {
      if (Config.EnableCache) {
        return;
      }

      await Utils.Storage.RemoveItem(Constant.CustomerDataKey);
      await Utils.Storage.RemoveItem(Constant.ProductDataKey);
      await Utils.Storage.RemoveItem(Constant.OrderDataKey);
    },
    []
  );

  const doAsyncWork = useCallback(async () => {
    await Utils.Storage.RemoveItem(Constant.IpAddressKey);
    await cleanLocalCache();
  }, [cleanLocalCache]);

  useEffect(() => {
    doAsyncWork().then(() => {
      const bodyStyles = document.body.style;
      bodyStyles.background = '#F2F2F2';
      const bodyClassList = document.body.classList;
      bodyClassList.add('notranslate');
    });
  }, [doAsyncWork]);

  const handleCleanCookie = () => {
    // TODO: we might not need these anymore
    Utils.Cookie.SetItem(Constant.CookieTokenKey, '');
    Utils.Cookie.SetItem(Constant.CookieRefreshTokenKey, '');
    Utils.Cookie.SetItem(Constant.CookieUserNameKey, '');
  };

  useEffect(() => {
    handleCleanCookie();
    onStart();

    return () => handleCleanCookie();
  }, [onStart]);

  if (!isReady) {
    return <React.Fragment />;
  }

  return (
    <GestureHandlerRootView>
      <FlagProvider config={unleashConfig}>
        <RecoilRoot>
          <NavigationContainer />
        </RecoilRoot>
      </FlagProvider>
    </GestureHandlerRootView>
  );
};

export default App;
